import { Briefcase, Check, Pin, Type } from "lucide-react";
import { useState, type FC, type KeyboardEvent } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { NoteAvatar } from "./NoteEntry";

export type CaseOption = { id: string; label: string };

export type NoteDraft = {
  body: string;
  pinned: boolean;
  caseId: string | null;
};

type Props = {
  authorName: string;
  avatar?: string;
  cases: CaseOption[];
  maxChars: number;
  /** Resolves true once saved, so the draft clears. */
  onSubmit: (draft: NoteDraft) => Promise<boolean>;
};

const toggleClass = (on: boolean) =>
  cn(
    "h-6 gap-1 px-2 text-sm",
    on ? "bg-fill-2 text-fg inset-ring-line-3" : "text-fg-2"
  );

/** The composer on top of the timeline: Markdown text, Pin, an optional case link, ⌘↵ to add. */
export const NoteComposer: FC<Props> = ({
  authorName,
  avatar,
  cases,
  maxChars,
  onSubmit
}) => {
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const linked = cases.find((c) => c.id === caseId) ?? null;
  const tooLong = body.length > maxChars;
  const canAdd = Boolean(body.trim()) && !tooLong && !saving;

  const add = async () => {
    if (!canAdd) return;
    setSaving(true);
    try {
      const saved = await onSubmit({ body: body.trim(), pinned, caseId });
      if (saved) {
        setBody("");
        setPinned(false);
        setCaseId(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void add();
    }
  };

  return (
    <div
      data-testid="note-composer"
      className="flex flex-col gap-2 rounded-xl bg-elevated p-3 ring-1 ring-line-2 focus-within:ring-line-3"
    >
      <div className="flex gap-2.5">
        <NoteAvatar name={authorName} image={avatar} size="sm" />
        <textarea
          aria-label="New Note"
          data-testid="note-composer-text"
          value={body}
          rows={2}
          placeholder="Add a note…"
          aria-invalid={tooLong || undefined}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          className="field-sizing-content min-h-11 min-w-0 flex-1 resize-none bg-transparent pt-0.5 text-md text-fg outline-none placeholder:text-fg-4"
        />
      </div>
      <div className="flex flex-wrap items-center gap-1 pl-8.5">
        {cases.length ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="pill"
                size="xs"
                className={toggleClass(!!linked)}
              >
                <Briefcase />
                {linked ? linked.label : "Link a Case"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {cases.map((c) => (
                <DropdownMenuItem key={c.id} onSelect={() => setCaseId(c.id)}>
                  {c.label}
                  {c.id === caseId ? <Check className="ml-auto" /> : null}
                </DropdownMenuItem>
              ))}
              {linked ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setCaseId(null)}>
                    Remove Case Link
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        <Button
          variant="pill"
          size="xs"
          aria-pressed={pinned}
          className={toggleClass(pinned)}
          onClick={() => setPinned((p) => !p)}
        >
          <Pin />
          {pinned ? "Pinned" : "Pin"}
        </Button>
        <span className="ml-1.5 hidden items-center gap-1 text-sm text-fg-4 sm:inline-flex">
          <Type className="size-3" />
          Markdown supported
        </span>
        <div className="ml-auto flex items-center gap-2">
          {tooLong ? (
            <span className="text-sm text-danger">
              {body.length.toLocaleString()} / {maxChars.toLocaleString()}
            </span>
          ) : null}
          <KbdGroup>
            <Kbd>⌘</Kbd>
            <Kbd>↵</Kbd>
          </KbdGroup>
          <Button
            variant="primary"
            size="sm"
            disabled={!canAdd}
            data-testid="note-composer-add"
            onClick={() => void add()}
          >
            {saving ? <Spinner className="size-3.5" /> : null}
            Add Note
          </Button>
        </div>
      </div>
    </div>
  );
};
